/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import dayjs from 'dayjs';
import { ILoggerService, LogLevel } from './ILoggerService';
import { ILoggerTransport } from './ILoggerTransport';
import { ConsoleLoggerTransport } from './ConsoleLoggerTransport';
import { ILogCallLocation, ILogEntry } from './ILogEntry';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class DefaultLoggerService implements ILoggerService {
	public static logLineNumbers: boolean = false;

	private _context?: string;
	private _levels?: LogLevel[];
	private _transports: ILoggerTransport[] = [new ConsoleLoggerTransport()];

	public constructor(context?: string) {
		this._context = context;
	}

	public getContext(): string | undefined {
		return this._context;
	}

	public setContext(context: string): void {
		this._context = context;
	}

	public addTransport(transport: ILoggerTransport): void {
		this._transports.push(transport);
	}

	public removeTransport(transport: ILoggerTransport): void {
		const index = this._transports.indexOf(transport);
		if (index > -1) {
			this._transports.splice(index, 1);
		}
	}

	public getTransports(): ILoggerTransport[] {
		return this._transports;
	}
	/**
	 * Resets the logger context to the value that was passed in the constructor.
	 */
	public resetContext(): void {
		this._context = undefined;
	}

	protected printMessage(level: LogLevel, message: any, ...optionalParams: any[]): void {
		if (this._levels && this._levels.indexOf(level) === -1) return;

		const timestamp = dayjs().toISOString();
		const context = this._context ? ` [${this._context}]` : '';
		const logLevel = `[${level.toUpperCase()}]`;

		const logCallLocation: ILogCallLocation | undefined = this.getCallerLocation();

		const prefix = `${timestamp} ${logLevel}${context}`;

		const logEntry: ILogEntry = {
			logLevel: level,
			prefix,
			logCallLocation,
			message,
			optionalParams,
		};

		// Send log entry to transports
		for (const transport of this._transports) {
			transport.transportMessage(logEntry);
		}
	}

	public log(message: any, ...optionalParams: any[]): any {
		this.printMessage('log', message, ...optionalParams);
	}

	public error(message: any, ...optionalParams: any[]): any {
		this.printMessage('error', message, ...optionalParams);
	}

	public warn(message: any, ...optionalParams: any[]): any {
		this.printMessage('warn', message, ...optionalParams);
	}

	public debug(message: any, ...optionalParams: any[]): any {
		this.printMessage('debug', message, ...optionalParams);
	}

	public verbose(message: any, ...optionalParams: any[]): any {
		this.printMessage('verbose', message, ...optionalParams);
	}

	public setLogLevels?(levels: LogLevel[]): any {
		this._levels = levels;
	}

	/**
	 * This function generates a stack-trace internally and parses it, top-down, looking for the
	 * first stack frame from the top that is for a function call NOT originating from this file.
	 * When found, the function name, filename and line number of that stack frame is returned
	 * via a new ILogCallLocation object.
	 * If no such stack frame is found (which will be unlikely), undefined is returned.
	 */
	public getCallerLocation(): ILogCallLocation | undefined {
		if (DefaultLoggerService.logLineNumbers) {
			const err = new Error();
			const stackStrings = err.stack?.split('\n');

			if (stackStrings) {
				// We return the filename and line-num from the frame AFTER calls into this class' functions:
				const capturingRegEx = /.*at ([^ ]*) .*\/([^\/]*):(\d+):\d+\)/;
				for (const stackString of stackStrings) {
					const found = stackString.match(capturingRegEx);
					if (found && found[0]) {
						if (!found[1].startsWith('DefaultLoggerService.')) {
							return {
								functionName: found[1],
								fileName: found[2],
								lineNumber: found[3],
							} as ILogCallLocation;
						}
					}
				}
			}
		}
		return undefined;
	}
}

export let logger: ILoggerService = new DefaultLoggerService();

export function setLoggerService(newLogger: ILoggerService): void {
	logger = newLogger;
}

export function setLogLineNumbers(logLineNumbers: boolean): void {
	DefaultLoggerService.logLineNumbers = logLineNumbers;
}
